import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import anime from 'animejs';
import axios from '../api/axiosClient';
import { AuthContext } from '../context/AuthContext';
import { logger } from '../utils/logger';
import PublicNavbar from '../components/public/PublicNavbar';
import PublicFooter from '../components/public/PublicFooter';
import { useTranslation } from 'react-i18next';

// Using logo from public folder
const logo = '/images/logos/Hairchitech_Logo1.png';

const HomePage = () => {
  const { t } = useTranslation('marketing');
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const location = useLocation();
  const [currentDemo, setCurrentDemo] = useState(0);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showSignInPanel, setShowSignInPanel] = useState(false);
  const [activeTab] = useState('login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const heroRef = useRef(null);
  const socialProofRef = useRef(null);
  const productDemoRef = useRef(null);
  const statsRef = useRef(null);

  // Demo rotation for interactive dashboard
  const demos = [
    { title: t('marketing.demo_0_title'), content: t('marketing.demo_0_content') },
    { title: t('marketing.demo_1_title'), content: t('marketing.demo_1_content') },
    { title: t('marketing.demo_2_title'), content: t('marketing.demo_2_content') }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDemo((prev) => (prev + 1) % demos.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [demos.length]);

  // Open sign-in panel if URL requests auth prompt
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auth') === '1') {
      openSignInPanel();
    }
  }, [location.search]);

  // Enhanced entrance animations
  useEffect(() => {
    // Hero section animation
    anime({
      targets: '.hero-element',
      translateY: [60, 0],
      opacity: [0, 1],
      duration: 800,
      delay: anime.stagger(200, { start: 300 }),
      easing: 'easeOutExpo'
    });

    // Navigation slide down
    anime({
      targets: 'nav',
      translateY: [-100, 0],
      opacity: [0, 1],
      duration: 800,
      easing: 'easeOutExpo'
    });
  }, []);

  // Scroll-triggered animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === socialProofRef.current) {
            anime({
              targets: '.company-logo',
              scale: [0.8, 1],
              opacity: [0, 1],
              duration: 600,
              delay: anime.stagger(100),
              easing: 'easeOutQuart'
            });
          }

          if (entry.target === productDemoRef.current) {
            anime({
              targets: '.product-section',
              translateY: [40, 0],
              opacity: [0, 1],
              duration: 800,
              delay: anime.stagger(300),
              easing: 'easeOutExpo'
            });
          }

          if (entry.target === statsRef.current) {
            anime({
              targets: '.stat-number',
              innerHTML: (el) => [0, el.innerHTML],
              duration: 2000,
              round: 1,
              easing: 'easeOutExpo'
            });
          }

          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (socialProofRef.current) observer.observe(socialProofRef.current);
    if (productDemoRef.current) observer.observe(productDemoRef.current);
    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  const handleButtonHover = (e, isEntering) => {
    anime({
      targets: e.currentTarget,
      scale: isEntering ? 1.02 : 1,
      duration: 200,
      easing: 'easeOutQuart'
    });
  };

  const openSignInPanel = () => {
    setShowSignInPanel(true);
    document.body.style.overflow = 'hidden';
  };

  const closeSignInPanel = () => {
    setShowSignInPanel(false);
    document.body.style.overflow = 'auto';
  };

  // Cleanup: ensure body overflow is reset when component unmounts or panel closes
  useEffect(() => {
    if (!showSignInPanel) {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showSignInPanel]);

  const handleSignInClick = (e) => {
    e.preventDefault();
    openSignInPanel();
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const res = await axios.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      
      if (res.data?.ok === false || !res.data?.token) {
        if (res.data?.must_set_password) {
          setError('Please set your password using the email we sent you.');
        } else {
          setError('Email or password is incorrect. Please try again.');
        }
        setLoading(false);
        return;
      }

      const { token, user, redirect } = res.data;
      login(user, token);
      logger.debug('Login success', { role: user?.role, redirect });
      
      closeSignInPanel();
      setFormData({ name: '', email: '', password: '' });
      
      // Use backend-provided redirect or fallback to role-based routing
      if (redirect) {
        navigate(redirect);
      } else {
        // Fallback logic for backward compatibility
        const role = user.role?.toLowerCase();
        if (role === 'superadmin') {
          navigate('/superadmin');
        } else if (role === 'shop_admin' || role === 'barber') {
          navigate('/admin');
        } else if (role === 'client') {
          navigate('/app/book');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      logger.error('Login error', { message: err?.message, status: err?.response?.status });
      if (err?.response?.data?.must_set_password) {
        setError('Please set your password using the email we sent you.');
      } else {
        setError('Email or password is incorrect. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotPasswordLoading(true);
    
    try {
      await axios.post('/auth/forgot-password', {
        email: forgotPasswordEmail
      });
      logger.debug('Forgot password requested');
      
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail('');
    } catch (err) {
      logger.error('Forgot password error', { message: err?.message, status: err?.response?.status });
      setError(err.response?.data?.message || 'Failed to send reset email.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const openForgotPassword = () => {
    setShowForgotPassword(true);
    setForgotPasswordSuccess(false);
    setError('');
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordSuccess(false);
    setError('');
  };


  // Close panel with Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSignInPanel) {
        closeSignInPanel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSignInPanel]);

  return (
    <div className="min-h-screen bg-transparent relative">
      {/* Sign-in Panel - Hairchitech Style */}
      <div className={`fixed top-0 right-0 h-full w-full md:w-1/2 lg:w-1/2 z-[9999] transform transition-transform duration-500 ease-out ${
        showSignInPanel ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Background with Hairchitech gradient */}
        <div className="h-full relative overflow-hidden">
          {/* Hairchitech gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}></div>
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-white transform skew-y-6 origin-bottom-left"></div>
          
          {/* Content Container */}
          <div className="relative h-full flex items-center justify-center p-4 sm:p-6 md:p-8">
            {/* White Login Card */}
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
              {/* Header */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex items-center justify-center mb-3 sm:mb-4">
                  <Link to="/">
                    <img src={logo} alt="Hairchitech" className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 cursor-pointer" />
                  </Link>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {t('marketing.sign_in_title')}
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm">
                  {t('marketing.sign_in_subtitle')}
                </p>
              </div>

              {/* Sign In Header - No tabs, just sign in */}
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg font-semibold text-gray-900 text-center">{t('marketing.sign_in')}</h2>
              </div>

              {/* Login Form */}
              {activeTab === 'login' && (
                <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
                  {error && <p className="text-red-500 text-xs sm:text-sm text-center">{error}</p>}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {t('marketing.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-c9a038 focus:border-c9a038 transition-all duration-200 text-sm sm:text-base"
                      placeholder={t('marketing.email_placeholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      {t('marketing.password')}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleFormChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-c9a038 focus:border-c9a038 transition-all duration-200 text-sm sm:text-base"
                      placeholder={t('marketing.password_placeholder')}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 sm:py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    style={{ background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)' }}
                    onMouseEnter={(e) => handleButtonHover(e, true)}
                    onMouseLeave={(e) => handleButtonHover(e, false)}
                  >
                    {loading ? t('marketing.signing_in') : t('marketing.sign_in_btn')}
                  </button>

                  <div className="text-center pt-3 sm:pt-4">
                    <p className="text-xs sm:text-sm text-gray-600">
                      <button 
                        type="button"
                        onClick={openForgotPassword}
                        style={{ color: '#c9a038' }}
                        className="hover:opacity-80 font-medium transition-colors"
                      >
                        {t('marketing.forgot_password')}
                      </button>
                    </p>
                  </div>

                  {/* New User CTA */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-600 text-center mb-3">
                      <strong>{t('marketing.new_to_hairchitech')}</strong>
                    </p>
                    <p className="text-xs text-gray-500 text-center mb-3">
                      {t('marketing.choose_plan')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        closeSignInPanel();
                        navigate('/pricing');
                      }}
                      className="w-full py-2 px-4 rounded-lg font-medium border-2 transition-all duration-200"
                      style={{ borderColor: '#c9a038', color: '#c9a038' }}
                    >
                      {t('marketing.view_pricing_plans')}
                    </button>
                  </div>
                </form>
              )}

            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={closeSignInPanel}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white hover:text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('marketing.reset_password')}</h2>
              <button
                onClick={closeForgotPassword}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {forgotPasswordSuccess ? (
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{t('marketing.check_email_title')}</h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                  {t('marketing.check_email_body')}
                </p>
                <button
                  onClick={closeForgotPassword}
                  className="w-full py-2 sm:py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 text-sm sm:text-base"
                  style={{ background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)' }}
                >
                  {t('marketing.close')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                {error && <p className="text-red-500 text-xs sm:text-sm text-center mb-4">{error}</p>}
                <div className="mb-4 sm:mb-6">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    {t('marketing.email_address')}
                  </label>
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-c9a038 focus:border-c9a038 transition-all duration-200 text-sm sm:text-base"
                    placeholder={t('marketing.email_address_placeholder')}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="w-full py-2 sm:py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  style={{ background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)' }}
                >
                  {forgotPasswordLoading ? t('marketing.sending') : t('marketing.send_reset_link')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <PublicNavbar onSignInClick={handleSignInClick} />

      {/* Hero Section - Luxury AI Company Style */}
      <section className="relative overflow-hidden min-h-screen" ref={heroRef} style={{ clipPath: 'polygon(0 0, 100% 0, 100% 95%, 0 95%)' }}>
        {/* Luxury Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}></div>

        {/* ✅ NEW Diagonal stripe background */}
        <div
          className="absolute top-0 right-0 h-[150%] w-1 z-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom left, rgba(201,160,56,0.3) 0%, transparent 80%)',
            transform: 'translate(50%, -50%) rotate(45deg)',
            transformOrigin: 'top right'
          }}
        ></div>

        {/* Abstract Shapes */}
        <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl"></div>

        {/* Floating Abstract Lines */}
        <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.3) 0%, transparent 100%)' }}></div>
        <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }}></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-32 min-h-screen flex items-start">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start w-full mt-16">

            {/* Left Side - Text Content */}
            <div>
              <div className="hero-element inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border text-gray-200 text-sm font-medium mb-8" style={{ borderColor: 'rgba(201, 160, 56, 0.3)' }}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#c9a038' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('marketing.hero_badge')}
              </div>

              <h1 className="hero-element text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                {t('marketing.hero_title_1')}
                <span className="block bg-gradient-to-r bg-clip-text text-transparent" style={{ background: 'linear-gradient(90deg, #c9a038 0%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {t('marketing.hero_title_2')}
                </span>
                <span className="block">{t('marketing.hero_title_3')}</span>
              </h1>

              <p className="hero-element text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed font-light max-w-2xl">
                {t('marketing.hero_subtitle')}
              </p>

              <div className="hero-element flex flex-col sm:flex-row items-center gap-4 mb-12">
                <Link
                  to="/pricing"
                  className="px-8 py-4 text-white rounded-lg text-lg font-semibold hover:opacity-90 transition-all duration-200 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)' }}
                  onMouseEnter={(e) => handleButtonHover(e, true)}
                  onMouseLeave={(e) => handleButtonHover(e, false)}
                >
                  {t('marketing.view_pricing')}
                </Link>
                <button
                  onClick={openSignInPanel}
                  className="px-8 py-4 border-2 text-white rounded-lg text-lg font-semibold hover:bg-white/10 transition-all duration-200 backdrop-blur-sm"
                  style={{ borderColor: '#c9a038' }}
                  onMouseEnter={(e) => handleButtonHover(e, true)}
                  onMouseLeave={(e) => handleButtonHover(e, false)}
                >
                  {t('marketing.sign_in')}
                </button>
                
                {/* Today Card */}
                <div className="hero-element backdrop-blur-sm rounded-lg p-2.5 shadow-lg border border-white/20 transform hover:scale-105 transition-all duration-500 z-20">
                  <div className="text-center">
                    <div className="text-sm font-bold text-white mb-0.5">{t('marketing.today')}</div>
                    <div className="text-lg font-bold mb-0.5" style={{ color: '#c9a038' }}>1,247</div>
                    <div className="text-xs text-white/80 leading-tight">{t('marketing.ai_transformations')}</div>
                    <div className="text-xs font-medium" style={{ color: '#c9a038' }}>+32.8% ↗</div>
                  </div>
                </div>
              </div>

              {/* Video Section in Hero */}
              <div className="hero-element mt-16">
                <div className="relative max-w-2xl mx-auto">
                  <div className="relative rounded-xl overflow-hidden shadow-2xl">
                    <video 
                      className="w-full h-auto"
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      style={{ maxHeight: '600px' }}
                    >
                      <source src="/videos/Hairchitech_AI_Video1.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    
                    {/* Video overlay gradient */}
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Floating UI Elements */}
            <div className="relative lg:block">
              {/* Main Dashboard Card */}
              <div className="relative z-10 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl border border-white/20 p-4 sm:p-6 lg:p-8 transform hover:scale-105 transition-all duration-500 mx-4 lg:mx-0">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="flex items-center">
                    <Link to="/">
                      <img src={logo} alt="Hairchitech" className="h-6 w-6 sm:h-8 sm:w-8 cursor-pointer" />
                    </Link>
                    <span className="ml-2 sm:ml-3 font-semibold text-white text-sm sm:text-base">{t('marketing.ai_dashboard')}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      logger.debug('Dashboard toggle button clicked', { animeAvailable: !!anime });
                      
                      // Visible anime.js animations
                      anime({
                        targets: e.currentTarget,
                        scale: [1, 0.8, 1.2, 1],
                        rotate: [0, -10, 10, 0],
                        duration: 600,
                        easing: 'easeOutElastic(1, .6)',
                        complete: () => {
                          logger.debug('Dashboard toggle button animation completed');
                        }
                      });

                      // Icon animation
                      anime({
                        targets: e.currentTarget.querySelector('svg'),
                        rotateY: [0, 360],
                        duration: 800,
                        easing: 'easeOutBack(1.7)'
                      });

                      // Pulse effect
                      anime({
                        targets: e.currentTarget,
                        boxShadow: [
                          '0 10px 40px rgba(201, 160, 56, 0.3)',
                          '0 20px 60px rgba(201, 160, 56, 0.6)',
                          '0 10px 40px rgba(201, 160, 56, 0.3)'
                        ],
                        duration: 400,
                        easing: 'easeInOutQuart'
                      });

                      // Toggle dashboard with slight delay for effect
                      setTimeout(() => {
                        setShowDashboard(prev => {
                          logger.debug('Dashboard state changing', { next: !prev });
                          return !prev;
                        });
                      }, 200);
                    }}
                    onMouseEnter={(e) => {
                      anime({
                        targets: e.currentTarget,
                        scale: 1.05,
                        duration: 200,
                        easing: 'easeOutQuart'
                      });
                    }}
                    onMouseLeave={(e) => {
                      anime({
                        targets: e.currentTarget,
                        scale: 1,
                        duration: 200,
                        easing: 'easeOutQuart'
                      });
                    }}
                    style={{ zIndex: 9999 }} // Ensure it's on top
                    className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white p-2 sm:p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </button>
                </div>

                <div className="mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-1 sm:mb-2">{demos[currentDemo].title}</h3>
                  <p className="text-white/80 text-xs sm:text-sm">{demos[currentDemo].content}</p>
                </div>

                {/* BeforeAfter Slider Integration */}
                <div className="mb-4 sm:mb-6">
                  <BeforeAfterSlider 
                    showDashboard={showDashboard} 
                    onToggleDashboard={() => setShowDashboard(prev => !prev)} 
                  />
                </div>

                <div className="space-y-2 sm:space-y-4">
                  <div className="flex items-center justify-between p-2 sm:p-4 bg-white/10 backdrop-blur-sm rounded-md sm:rounded-lg border border-white/20">
                    <span className="font-medium text-white text-xs sm:text-sm">{t('marketing.face_analysis')}</span>
                    <div className="flex items-center">
                      <div className="w-12 sm:w-20 h-1.5 sm:h-2 rounded-full mr-2 sm:mr-3" style={{ background: 'linear-gradient(90deg, #c9a038 0%, #f59e0b 100%)' }}></div>
                      <span className="font-semibold text-xs sm:text-sm" style={{ color: '#c9a038' }}>99.7%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 sm:p-4 bg-white/10 backdrop-blur-sm rounded-md sm:rounded-lg border border-white/20">
                    <span className="font-medium text-white text-xs sm:text-sm">{t('marketing.style_matching')}</span>
                    <div className="flex items-center">
                      <div className="w-10 sm:w-16 h-1.5 sm:h-2 rounded-full mr-2 sm:mr-3" style={{ background: 'linear-gradient(90deg, #c9a038 0%, #7d6633 100%)' }}></div>
                      <span className="font-semibold text-xs sm:text-sm" style={{ color: '#c9a038' }}>98.9%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 sm:p-4 bg-white/10 backdrop-blur-sm rounded-md sm:rounded-lg border border-white/20">
                    <span className="font-medium text-white text-xs sm:text-sm">{t('marketing.processing_speed')}</span>
                    <div className="flex items-center">
                      <div className="w-8 sm:w-12 h-1.5 sm:h-2 rounded-full mr-2 sm:mr-3" style={{ background: 'linear-gradient(90deg, #c9a038 0%, #f59e0b 100%)' }}></div>
                      <span className="font-semibold text-xs sm:text-sm" style={{ color: '#c9a038' }}>2.3s</span>
                    </div>
                  </div>
                </div>
              </div>



              {/* Floating Feature Badge - Hidden on mobile */}
              <div className="hero-element absolute top-40 -left-20 text-white px-3 py-2 rounded-full shadow-lg transform -rotate-12 hover:rotate-0 transition-all duration-500 z-20 hidden lg:block" style={{ background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)' }}>
                <div className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  <span className="text-xs font-medium">{t('marketing.ai_powered')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Mockup Section */}
      <section className="pt-32 pb-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('marketing.mobile_title_1')}
              <span className="block" style={{ color: '#c9a038' }}>{t('marketing.mobile_title_2')}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('marketing.mobile_subtitle')}
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Mobile mockup container */}
            <div className="relative flex justify-center items-center">
              {/* Phone frame */}
              <div className="relative">
                <div className="w-80 h-[600px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Status bar */}
                    <div className="bg-gray-900 h-8 flex items-center justify-between px-6 text-white text-sm">
                      <span>9:41</span>
                      <div className="flex space-x-1">
                        <div className="w-4 h-2 bg-white rounded-sm"></div>
                        <div className="w-4 h-2 bg-white rounded-sm"></div>
                        <div className="w-4 h-2 bg-white rounded-sm"></div>
                      </div>
                    </div>

                    {/* App interface */}
                    <div className="p-6 h-full bg-gradient-to-b from-gray-50 to-white">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center">
                          <Link to="/">
                            <img src={logo} alt="Hairchitech" className="h-8 w-8 cursor-pointer" />
                          </Link>
                          <span className="ml-2 text-lg font-semibold text-gray-900">Hairchitech</span>
                        </div>
                        <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      </div>

                      {/* Main content */}
                      <div className="text-center mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('marketing.try_new_hairstyles')}</h3>
                        <p className="text-gray-600 text-sm">{t('marketing.upload_photo')}</p>
                      </div>

                      {/* Camera interface */}
                      <div className="relative mb-6">
                        <div className="w-48 h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl mx-auto relative overflow-hidden">
                          <img 
                            src="/images/before-after/Phone_image.png" 
                            alt="Phone with hair analysis interface"
                            className="w-full h-full object-cover rounded-2xl"
                            style={{ objectPosition: 'center -5%' }}
                          />
                          {/* AI analysis overlay */}
                          <div className="absolute top-2 left-2 right-2 h-1 bg-gradient-to-r from-blue-400 to-green-400 rounded-full opacity-80"></div>
                          <div className="absolute top-4 left-2 text-xs text-gray-600 bg-white/80 px-2 py-1 rounded">
                            {t('marketing.ai_analyzing')}
                          </div>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="space-y-3">
                        <button
                          className="w-full py-4 rounded-xl text-white font-medium"
                          style={{ backgroundColor: '#c9a038' }}
                        >
                          {t('marketing.take_photo')}
                        </button>
                        <button className="w-full py-4 border-2 border-gray-200 rounded-xl text-gray-700 font-medium">
                          {t('marketing.choose_from_gallery')}
                        </button>
                      </div>

                      {/* Recent styles */}
                      <div className="mt-6">
                        <p className="text-sm text-gray-500 mb-3">{t('marketing.recent_styles')}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[1, 2, 3].map(i => (
                            <div key={i} className="aspect-square bg-gray-100 rounded-lg"></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating elements around phone */}
                <div className="absolute -top-4 -left-12 w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>

                <div className="absolute -bottom-6 -right-16 w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>

                <div className="absolute top-20 -right-8 w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Features around mockup */}
            <div className="absolute inset-0 flex items-center justify-between pointer-events-none">
              <div className="hidden lg:block bg-white rounded-xl p-6 shadow-lg border border-gray-200 max-w-xs -ml-32">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-900">{t('marketing.instant_analysis')}</span>
                </div>
                <p className="text-sm text-gray-600">{t('marketing.instant_analysis_desc')}</p>
              </div>

              <div className="hidden lg:block bg-white rounded-xl p-6 shadow-lg border border-gray-200 max-w-xs -mr-32">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="font-semibold text-gray-900">{t('marketing.try_on_ready')}</span>
                </div>
                <p className="text-sm text-gray-600">{t('marketing.try_on_ready_desc')}</p>
              </div>
            </div>
          </div>

          {/* App store badges */}
          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6">{t('marketing.download_app')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="bg-black text-white px-6 py-3 rounded-lg flex items-center space-x-3 cursor-pointer hover:bg-gray-800 transition-colors">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                </svg>
                <div>
                  <div className="text-xs">{t('marketing.download_on_the')}</div>
                  <div className="text-sm font-semibold">{t('marketing.app_store')}</div>
                </div>
              </div>
              <div className="bg-black text-white px-6 py-3 rounded-lg flex items-center space-x-3 cursor-pointer hover:bg-gray-800 transition-colors">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div>
                  <div className="text-xs">{t('marketing.get_it_on')}</div>
                  <div className="text-sm font-semibold">{t('marketing.google_play')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Sections - Modular like Stripe */}
      <section className="py-24 bg-gray-50" ref={productDemoRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          {/* AI Analysis */}
          <div className="product-section mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('marketing.ai_analysis_badge')}
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {t('marketing.ai_analysis_title')}
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {t('marketing.ai_analysis_body')}
                </p>
                <Link
                  to="/pricing"
                  className="inline-flex items-center px-6 py-3 rounded-md font-medium text-white transition-colors"
                  style={{ backgroundColor: '#c9a038' }}
                >
                  {t('marketing.view_pricing_link')}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <div className="mt-8 text-sm text-gray-500">
                  {t('marketing.see_also')}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium">{t('marketing.face_shape_analysis')}</span>
                    <span className="text-green-600 font-semibold">99.7% ✓</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium">{t('marketing.hair_texture_detection')}</span>
                    <span className="text-green-600 font-semibold">98.9% ✓</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium">{t('marketing.style_compatibility')}</span>
                    <span className="text-green-600 font-semibold">97.2% ✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Virtual Try-On */}
          <div className="product-section mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                  <div className="text-center">
                    <Link to="/">
                      <img src={logo} alt="Hairchitech Logo" className="w-32 h-32 mx-auto mb-4 object-cover cursor-pointer" />
                    </Link>
                    <div className="text-lg font-semibold mb-2">{t('marketing.real_time_preview')}</div>
                    <div className="text-gray-600">{t('marketing.upload_analyze_transform')}</div>
                    <div className="mt-6 grid grid-cols-3 gap-2">
                      {/* Column 1 - Upload images */}
                      <div className="space-y-2">
                        <img 
                          src="/images/gallery/Upload_1.png" 
                          alt="Upload step 1"
                          className="aspect-square object-cover rounded-lg"
                        />
                        <img 
                          src="/images/gallery/Upload_2.png" 
                          alt="Upload step 2"
                          className="aspect-square object-cover rounded-lg"
                        />
                      </div>
                      {/* Column 2 - Analyze images */}
                      <div className="space-y-2">
                        <img 
                          src="/images/gallery/Analyze_1.png" 
                          alt="Analyze step 1"
                          className="aspect-square object-cover rounded-lg"
                        />
                        <img 
                          src="/images/gallery/Analyze_2.png" 
                          alt="Analyze step 2"
                          className="aspect-square object-cover rounded-lg"
                        />
                      </div>
                      {/* Column 3 - Transform images */}
                      <div className="space-y-2">
                        <img 
                          src="/images/gallery/Transform_1.png" 
                          alt="Transform step 1"
                          className="aspect-square object-cover rounded-lg"
                        />
                        <img 
                          src="/images/gallery/Transform_2.png" 
                          alt="Transform step 2"
                          className="aspect-square object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 mb-6">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {t('marketing.virtual_try_on_badge')}
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {t('marketing.virtual_try_on_title')}
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {t('marketing.virtual_try_on_body')}
                </p>
                <Link
                  to="/pricing"
                  className="inline-flex items-center px-6 py-3 rounded-md font-medium text-white transition-colors"
                  style={{ backgroundColor: '#c9a038' }}
                >
                  {t('marketing.view_pricing_link')}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>

          {/* Salon Platform */}
          <div className="product-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-6">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t('marketing.salon_platform_badge')}
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {t('marketing.salon_platform_title')}
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {t('marketing.salon_platform_body')}
                </p>
                <Link
                  to="/pricing"
                  className="inline-flex items-center px-6 py-3 rounded-md font-medium text-white transition-colors"
                  style={{ backgroundColor: '#c9a038' }}
                >
                  {t('marketing.view_pricing_link')}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('marketing.todays_appointments')}</span>
                    <span className="text-2xl font-bold" style={{ color: '#c9a038' }}>24</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full"></div>
                      <div className="flex-1">
                        <div className="font-medium">Sarah Johnson</div>
                        <div className="text-sm text-gray-500">Color & Cut - 2:00 PM</div>
                      </div>
                      <div className="text-green-600 text-sm">{t('marketing.confirmed')}</div>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-purple-100 rounded-full"></div>
                      <div className="flex-1">
                        <div className="font-medium">Emma Davis</div>
                        <div className="text-sm text-gray-500">Style Consultation - 3:30 PM</div>
                      </div>
                      <div className="text-yellow-600 text-sm">{t('marketing.pending')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

            {/* Video Demo Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <video 
              className="w-full h-112 object-cover" 
              autoPlay
              loop
              muted
              preload="metadata"
            >
              <source src="/videos/Hairchitech_AI_Video3.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Global Scale Stats */}
      <section className="py-24 bg-white" ref={statsRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('marketing.stats_title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('marketing.stats_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="stat-number text-4xl md:text-5xl font-bold mb-2" style={{ color: '#c9a038' }}>50</div>
              <div className="text-gray-600">{t('marketing.stat_1_label')}</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-4xl md:text-5xl font-bold mb-2" style={{ color: '#c9a038' }}>99.9</div>
              <div className="text-gray-600">{t('marketing.stat_2_label')}</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-4xl md:text-5xl font-bold mb-2" style={{ color: '#c9a038' }}>150</div>
              <div className="text-gray-600">{t('marketing.stat_3_label')}</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-4xl md:text-5xl font-bold mb-2" style={{ color: '#c9a038' }}>500</div>
              <div className="text-gray-600">{t('marketing.stat_4_label')}</div>
            </div>
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            {t('marketing.cta_title')}
          </h2>
          <p className="text-xl text-white/90 mb-12">
            {t('marketing.cta_body')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="px-8 py-4 bg-white text-gray-900 rounded-md text-lg font-medium hover:bg-gray-50 transition-colors"
            >
              {t('marketing.view_pricing_link')}
            </Link>
            <button
              onClick={openSignInPanel}
              className="px-8 py-4 border-2 border-white text-white rounded-md text-lg font-medium hover:bg-white hover:text-gray-900 transition-all duration-200"
            >
              {t('marketing.sign_in')}
            </button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default HomePage; 
